/**
 * @module app.shared
 */

const debounced = new WeakMap();

function getOrCreateSubMap(map: Map<any, any> | WeakMap<any, any>, key: any) {
    let sub = map.get(key);
    if(!sub) {
        sub = new WeakMap();
        map.set(key, sub);
    }
    return sub;
}

function getDebounceKey(target: any, method: any): number | undefined {
    const map = getOrCreateSubMap(debounced, target);
    return map.get(method);
}

function setDebounceKey(target: any, method: any, value: number) {
    const map = getOrCreateSubMap(debounced, target);
    return map.set(method, value);
}

// TODO [Matt] setTimeout(f, 0) is NOT the same as Promise.resolve().then(f).
//  the promise strategy executes within the same task, whereas the setTimeout
//  strategy executes within a new task. That means, selecting Promomise.resolve()
//  over setTimeout in the 0ms case will properly debounce multiple calls within
//  the same task but also execute within the same task.
function debounceFunction(method: Function, milliseconds: number = 0) {
    return function(this: Object, ...args: any[]) {
        const key = getDebounceKey(this, method);
        if(key) {
            clearTimeout(key);
        }
        setDebounceKey(
            this, method,
            window.setTimeout(method.bind(this, ...args), milliseconds)
        );
    }
}

/**
 * @decorator debounce
 * Debounces a targeted method, causing it to be called only after it has not been called for a specified number of milliseconds.
 * @param {number} [milliseconds=0] The number of milliseconds to listen for new calls before calling the decorated. Defaults to 0.
 */
export function debounce(milliseconds: number = 0): MethodDecorator {
    return function(target: Object, methodName: string | symbol, descriptor: PropertyDescriptor) {
        const method = Reflect.get(target, methodName)
        if(typeof method === 'function') {
            descriptor.value = debounceFunction(method, milliseconds);
        }
        return descriptor;
    };
}
